import { EApplicationStatus, EFormTableMode } from '@/interfaces'
import { ApplicationsState, FormState } from '@/state'
import { applicationsStateActions, formStateActions } from '@/state/actions'
import { OfflineState } from '@/state/offline-management'
import { useComputed } from '@preact/signals-react'
import { Button, FormInstance, Modal, notification, Tooltip } from 'antd'
import { FaSave } from 'react-icons/fa'
import { RiCloseFill } from 'react-icons/ri'
import { consultarRPA } from '@/services/rpaService'
import { useNavigate } from 'react-router-dom'

export default function SectionFormTableHeader({
  form,
  showTitle,
}: {
  form: FormInstance
  showTitle?: boolean
}) {
  const navigate = useNavigate()

  const handleSaveSubsectionForm = async () => {
    try {
      ApplicationsState.loading.value = {
        ...ApplicationsState.loading.peek(),
        savingRow: true,
      }
      const subsectionFormValues = await form.validateFields()
      console.log('DEBUG FORM VALUES', subsectionFormValues) // LOG para depuración

      // Validar cédula con RPA antes de guardar (obligatorio)
      let cedula =
        subsectionFormValues.docNumber ||
        subsectionFormValues.documento ||
        subsectionFormValues.cedula ||
        subsectionFormValues.Cedula ||
        subsectionFormValues.CEDULA
      if (!cedula) {
        for (const key of Object.keys(subsectionFormValues)) {
          if (
            key.toLowerCase().includes('cedula') ||
            key.toLowerCase().includes('documento') ||
            key.toLowerCase().includes('docnumber')
          ) {
            cedula = subsectionFormValues[key]
            break
          }
        }
      }
      if (!cedula) {
        notification.error({
          message: 'Cédula requerida',
          description:
            'Debe ingresar la cédula o documento del miembro para validar en RPA.',
          duration: 6,
        })
        ApplicationsState.loading.value = {
          ...ApplicationsState.loading.peek(),
          savingRow: false,
        }
        return
      }
      try {
        const rpaResult = await consultarRPA([cedula])
        let rpaDetalleParsed
        try {
          rpaDetalleParsed = rpaResult.detalle ? JSON.parse(rpaResult.detalle) : null
        } catch (e) {
          rpaDetalleParsed = null
        }
        // Detectar error real en la consulta RPA
        const consultaFallida =
          rpaResult.error ||
          (rpaDetalleParsed && rpaDetalleParsed.status === 'FAILED') ||
          (rpaDetalleParsed && Array.isArray(rpaDetalleParsed.results) && rpaDetalleParsed.results.some((r: any) => r.status === 'FAILED' || Object.values(r.results || {}).some((s: any) => s?.status === 'error' || s?.error)))
        if (consultaFallida) {
          Modal.error({
            title: 'Error en la consulta RPA',
            content:
              'No se pudo validar la cédula en RPA. Por favor, revise los datos o intente nuevamente.\nDetalle: ' + (rpaResult.error || rpaResult.detalle),
            okText: 'Cerrar',
            maskClosable: false,
            closable: false,
            centered: true,
          })
          ApplicationsState.loading.value = {
            ...ApplicationsState.loading.peek(),
            savingRow: false,
          }
          return
        }
        // Detectar propiedad registrada en los sistemas consultados
        let tienePropiedad = false
        if (rpaDetalleParsed && Array.isArray(rpaDetalleParsed.results)) {
          for (const r of rpaDetalleParsed.results) {
            // VUR: propietarios con porcentajeParticipacion > 0
            const vur = r.results?.vur
            if (vur && vur.status === 'success' && Array.isArray(vur.data)) {
              for (const item of vur.data) {
                if (item.propietarios && item.propietarios.length > 0) {
                  tienePropiedad = true
                  break
                }
              }
            }
            // MiVivienda: tiene_inmuebles true
            const mivivienda = r.results?.mivivienda
            if (mivivienda && mivivienda.status === 'success' && mivivienda.tiene_inmuebles) {
              tienePropiedad = true
            }
          }
        }
        // Si está inhabilitado o tiene propiedad, mostrar solo el modal bloqueante y redirigir al home
        if (rpaResult.inhabilitado || tienePropiedad) {
          Modal.info({
            title: 'Ciudadano con propiedad registrada',
            content: (
              <div>
                El ciudadano consultado tiene propiedades registradas en aplicaciones externas y no puede continuar con el proceso.
              </div>
            ),
            okText: 'Aceptar',
            maskClosable: false,
            closable: false,
            centered: true,
            onOk: async () => {
              const applicationId = ApplicationsState.currentApplication.value?.id;
              console.debug('[DEBUG][deleteApplication] Modal inhabilitado, id:', applicationId);
              if (applicationId) {
                try {
                  await applicationsStateActions.deleteApplication(String(applicationId));
                  console.debug('[DEBUG][deleteApplication] Eliminación completada');
                } catch (error) {
                  console.error('[ERROR][deleteApplication] Falló la eliminación:', error);
                }
              }
              navigate('/');
            },
          })
          ApplicationsState.loading.value = {
            ...ApplicationsState.loading.peek(),
            savingRow: false,
          }
          return
        }
      } catch (err) {
        console.error('[ERROR] Fallo en consulta RPA:', err)
        Modal.error({
          title: 'Error al consultar RPA',
          content: 'No se pudo validar la cédula en RPA. Intente nuevamente.',
          okText: 'Cerrar',
        })
        ApplicationsState.loading.value = {
          ...ApplicationsState.loading.peek(),
          savingRow: false,
        }
        return
      }

      if (FormState.entityForm.peek().mode === EFormTableMode.CREATE) {
        await applicationsStateActions.createRowData(subsectionFormValues)
      }

      if (FormState.entityForm.peek().mode === EFormTableMode.UPDATE) {
        await applicationsStateActions.updateRowData(subsectionFormValues)
      }

      notification.success({
        message: 'Información guardada',
        description: 'La información ha sido guardado correctamente',
        placement: 'top',
      })
    } catch (error) {
      console.error('[ERROR] Fallo general al guardar:', error)
      ApplicationsState.loading.value = {
        ...ApplicationsState.loading.peek(),
        savingRow: false,
      }
      notification.error({
        message: 'Error al guardar',
        description: typeof error === 'object' && error !== null && 'message' in error ? (error as any).message : 'Ocurrió un error inesperado',
      })
    }
  }

  const entityText = useComputed(() => {
    return (
      FormState.currentEntity.value?.label ||
      FormState.currentEntity.value?.name
    )
  })

  const canSave = useComputed(() =>
    Boolean(
      FormState.entityForm.value.mode !== EFormTableMode.VIEW &&
        !OfflineState.isSynchronizing.value &&
        (FormState.currentEntity.value?.permission?.create ||
          FormState.currentEntity.value?.permission?.edit)
    )
  )

  return (
    <div className='form-header'>
      {showTitle ? (
        <div className='form-title'>{`GESTIÓN DE REGISTRO DE: ${entityText.value}`}</div>
      ) : (
        <div />
      )}
      <div className='subsection-form-extra-actions'>
        {canSave.value && (
          <Tooltip placement='bottomLeft' title='Guardar'>
            <Button
              size='large'
              shape='circle'
              icon={<FaSave size={16} />}
              onClick={handleSaveSubsectionForm}
              loading={ApplicationsState.loading.value.savingRow}
            />
          </Tooltip>
        )}
        {!ApplicationsState.loading.value.savingRow &&
          !OfflineState.isSynchronizing.value && (
            <Tooltip placement='bottomLeft' title='Cancelar'>
              <Button
                disabled={ApplicationsState.loading.value.savingRow}
                size='large'
                shape='circle'
                icon={<RiCloseFill size={30} />}
                onClick={formStateActions.closeSubsectionForm}
              />
            </Tooltip>
          )}
      </div>
    </div>
  )
}
